import { Color, Zone, ZoneType } from "@fairfleet/geotab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { ZoneDraft } from "@/pages/zones/models/ZoneDraft";
import { draftToZoneEntity } from "@/pages/zones/utils/zoneDraft";

function useInvalidateZones() {
  const queryClient = useQueryClient();
  // "zones" = lists, "zone" = single lookups (ZoneCell), "zoneTypes" = the type list with its counts.
  return () =>
    Promise.all(
      ["zones", "zone", "zoneTypes"].map(key => queryClient.invalidateQueries({ queryKey: [key] })),
    );
}

export function useSaveZone() {
  const { geotabClient } = useGeotabClient();
  const invalidate = useInvalidateZones();

  return useMutation({
    mutationFn: async ({ draft, original }: { draft: ZoneDraft; original?: Zone }): Promise<string> => {
      const entity = draftToZoneEntity(draft, original);
      if (original) {
        await geotabClient.call("Set", { typeName: "Zone", entity });
        return original.id;
      }
      return geotabClient.call<string>("Add", { typeName: "Zone", entity });
    },
    onSuccess: invalidate,
  });
}

export function useRemoveZone() {
  const { geotabClient } = useGeotabClient();
  const invalidate = useInvalidateZones();

  return useMutation({
    mutationFn: async (zone: Pick<Zone, "id">) => {
      await geotabClient.call("Remove", { typeName: "Zone", entity: { id: zone.id } });
    },
    onSuccess: invalidate,
  });
}

export interface SaveZoneTypeInput {
  name: string;
  comment: string;
  fillColor: Color;
  original?: ZoneType;
}

export function useSaveZoneType() {
  const { geotabClient } = useGeotabClient();
  const invalidate = useInvalidateZones();

  return useMutation({
    mutationFn: async ({ name, comment, fillColor, original }: SaveZoneTypeInput): Promise<string> => {
      const entity = { ...(original ?? {}), name: name.trim(), comment, fillColor };
      if (original) {
        await geotabClient.call("Set", { typeName: "ZoneType", entity });
        return original.id;
      }
      return geotabClient.call<string>("Add", { typeName: "ZoneType", entity });
    },
    onSuccess: invalidate,
  });
}

export function useRemoveZoneType() {
  const { geotabClient } = useGeotabClient();
  const invalidate = useInvalidateZones();

  return useMutation({
    mutationFn: async (type: Pick<ZoneType, "id">) => {
      await geotabClient.call("Remove", { typeName: "ZoneType", entity: { id: type.id } });
    },
    onSuccess: invalidate,
  });
}
